import { MouseEvent } from "react";

import {
  MessageBoxStyles,
  MessageBoxWidgetDto,
  OfferDto,
  ProgressBarStyles,
  ProgressBarWidgetDto,
  QuantityButtonsStyles,
  QuantityButtonsWidgetDto,
  WidgetDto,
  WidgetType,
} from "@intelligems/ig-types";
import { hexToRgbA } from "@intelligems/ig-utils";
import { useTheme } from "@mui/material";
import { useThemeGlobalColors } from "@src/api/shopify-themes/get-theme-asset";

import { ColorFieldsConfig } from "../types";
import {
  getColorForDisplay,
  getPrimaryColorFromConfig,
} from "../utils/colorHierarchy";

interface UseColorConfigurationProps {
  widgetType: WidgetType;
  widget: MessageBoxWidgetDto | QuantityButtonsWidgetDto | ProgressBarWidgetDto;
  colorTheme: QuantityButtonsStyles | ProgressBarStyles | MessageBoxStyles;
  quantityButtonVariant?: string;
  offer?: OfferDto;
}

export const useColorConfiguration = ({
  widgetType,
  colorTheme,
  widget,
  quantityButtonVariant,
  offer,
}: UseColorConfigurationProps) => {
  const { colors: themeColors } = useThemeGlobalColors();
  const theme = useTheme();

  const getPrimaryColor = () => {
    return getPrimaryColorFromConfig(
      widget,
      colorTheme,
      themeColors,
      theme.palette.common.black
    );
  };

  const getColorForDisplayLocal = (
    field: string,
    styleOverrideColor: string | undefined,
    defaultColor: string = theme.palette.common.black
  ) => {
    const widgetColors =
      widget?.config?.desktop?.styles &&
      "colors" in widget.config.desktop.styles
        ? widget.config.desktop.styles.colors
        : undefined;
    const primaryColor = getPrimaryColor();

    return getColorForDisplay(
      field,
      styleOverrideColor,
      widgetColors,
      primaryColor,
      defaultColor
    );
  };

  const getColorForDisplayWithOpacity = (
    field: string,
    styleOverrideColor: string | undefined,
    defaultColor: string,
    opacity: number
  ) => {
    const baseColor = getColorForDisplayLocal(
      field,
      styleOverrideColor,
      defaultColor
    );
    return hexToRgbA(baseColor, opacity);
  };

  const colorFieldsConfig: ColorFieldsConfig = {
    widgetType,
    widget,
    quantityButtonVariant,
    offer,
    getColorForDisplayLocal,
    getColorForDisplayWithOpacity,
    colorTheme,
    themeColors: {
      black: theme.palette.common.black,
      white: theme.palette.common.white,
    },
  };

  return {
    getPrimaryColor,
    getColorForDisplayLocal,
    getColorForDisplayWithOpacity,
    colorFieldsConfig,
    themeColors,
  };
};
